import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';

interface TimetableEntry {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string | null;
}

export function ScanTimetablePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId: ownChildId } = useAuthStore();
  // Present when a parent is scanning a specific child's timetable
  // (/parent/child/:childId/timetable/scan); absent for the student's own
  // self-service route (/student/timetable/scan), which falls back to their
  // own id from the auth store.
  const { childId: paramChildId } = useParams<{ childId?: string }>();
  const childId = paramChildId ?? ownChildId;
  const basePath = paramChildId ? `/parent/child/${paramChildId}/timetable` : '/student/timetable';

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !childId) return;

    setScanError(null);
    const reader = new FileReader();

    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      const mediaType = dataUrl.split(';')[0]?.split(':')[1] ?? 'image/jpeg';
      if (!base64) {
        setScanError(t('scan_timetable.read_error'));
        return;
      }

      setIsScanning(true);
      try {
        const res = await apiFetch<{ entries: TimetableEntry[] }>(
          `/children/${childId}/timetable/ocr`,
          {
            method: 'POST',
            body: JSON.stringify({ image_base64: base64, media_type: mediaType }),
          },
        );
        navigate(`${basePath}/edit`, {
          state: { scannedEntries: res.entries ?? [] },
        });
      } catch {
        setIsScanning(false);
        setScanError(t('scan_timetable.ocr_failed'));
      }
    };

    reader.onerror = () => {
      setScanError(t('scan_timetable.read_error'));
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute={basePath} customTitle={t('scan_timetable.title')} />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          {/* Camera input — opens rear camera directly on mobile */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {/* Gallery/files input — shows file picker so the student can choose an existing photo */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {/* ── Scanning state ── */}
          {isScanning && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', flex: 1, padding: '48px 24px', textAlign: 'center',
            }}>
              <div style={{
                fontSize: '3.5rem', marginBottom: '24px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}>
                🤖
              </div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
                {t('scan_timetable.scanning_title')}
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                {t('scan_timetable.scanning_desc')}
              </p>
            </div>
          )}

          {/* ── Upload area ── */}
          {!isScanning && (
            <>
              {/* Scan with camera */}
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  padding: '36px 24px',
                  borderRadius: '16px',
                  border: '2px dashed var(--primary)',
                  background: 'var(--primary-light, #EEF2FF)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  marginBottom: '12px',
                }}
              >
                <span style={{ fontSize: '3.5rem', lineHeight: 1 }}>📷</span>
                <div>
                  <p style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.05rem', margin: '0 0 4px' }}>
                    {t('scan_timetable.scan_camera_title')}
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
                    {t('scan_timetable.scan_camera_desc')}
                  </p>
                </div>
              </button>

              {/* Upload from gallery / files */}
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  padding: '36px 24px',
                  borderRadius: '16px',
                  border: '2px dashed var(--border)',
                  background: 'white',
                  cursor: 'pointer',
                  textAlign: 'center',
                  marginBottom: '24px',
                }}
              >
                <span style={{ fontSize: '3.5rem', lineHeight: 1 }}>🖼️</span>
                <div>
                  <p style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '1.05rem', margin: '0 0 4px' }}>
                    {t('scan_timetable.upload_title')}
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
                    {t('scan_timetable.upload_desc')}
                  </p>
                </div>
              </button>

              {scanError && (
                <div style={{
                  padding: '12px 16px', borderRadius: '10px',
                  background: '#FEF2F2', color: 'var(--danger)',
                  fontSize: '0.875rem', lineHeight: 1.5, marginBottom: '16px',
                }}>
                  ⚠️ {scanError}
                </div>
              )}

              <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 16px' }}>
                {t('scan_timetable.or_divider')}
              </p>

              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate(`${basePath}/edit`)}
                style={{ width: '100%' }}
              >
                {t('scan_timetable.enter_manually')}
              </button>
            </>
          )}

        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(0.92); }
        }
      `}</style>
    </div>
  );
}
